import { useMemo, useState } from "react";
import { useAdminAudit } from "@/api/useAdmin";
import { AdminHeader } from "./AdminHeader";
import { AdminKpis } from "./AdminKpis";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";

/**
 * Audit log — append-only event feed scoped to admin + brand-changing
 * actions. Filter by severity + free-text. CSV export gives the
 * compliance team an evidence artefact (ISO 27001 A.12.4 / SOC 2 CC7.2).
 */
export function AuditPage() {
  const { data: audit = [], isLoading } = useAdminAudit();
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState<"all" | "info" | "warn" | "crit">("all");

  const filtered = useMemo(
    () =>
      audit.filter(
        (a) =>
          (severity === "all" || a.severity === severity) &&
          (!q ||
            a.target.toLowerCase().includes(q.toLowerCase()) ||
            a.actor.name.toLowerCase().includes(q.toLowerCase()) ||
            a.what.toLowerCase().includes(q.toLowerCase())),
      ),
    [audit, q, severity],
  );

  const exportCsv = () => {
    const header = "timestamp_utc,actor,role,severity,action,target,detail\n";
    const rows = filtered
      .map((a) =>
        [a.ts, a.actor.name, a.actor.role, a.severity, a.what, a.target, a.detail]
          .map((s) => `"${(s ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cura-studio-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <AdminHeader
        eyebrow="Studio admin · Audit"
        title="Audit log"
        subtitle="Append-only record of admin actions and client brand changes. Aligns with ISO 27001 (A.12.4) and SOC 2 CC7.2. Exportable for evidence."
        actions={
          <Button variant="secondary" onClick={exportCsv}>
            ↓ Export CSV
          </Button>
        }
      />

      <AdminKpis />

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search action, actor or target…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-w-[260px] rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          />
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as typeof severity)}
            className="rounded border border-[var(--border-2)] bg-white px-3 py-2 text-sm outline-none focus-visible:border-fg-1 focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          >
            <option value="all">All severities</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="crit">Critical</option>
          </select>
        </div>
        <div className="text-xs text-fg-3">
          {filtered.length} of {audit.length} entries
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        <div className="grid grid-cols-[140px_160px_1fr] gap-4 border-b border-[var(--border-1)] bg-bg-2 px-4 py-2 text-xxs font-semibold uppercase tracking-wider text-fg-3">
          <div>Timestamp (UTC)</div>
          <div>Actor</div>
          <div>What changed</div>
        </div>
        {isLoading ? (
          <div className="p-6 text-fg-3">Loading…</div>
        ) : (
          filtered.map((a) => (
            <div
              key={a.id}
              className="grid grid-cols-[140px_160px_1fr] items-start gap-4 border-b border-[var(--border-1)] px-4 py-3 text-sm last:border-0"
            >
              <div className="font-mono text-xs tabular-nums text-fg-3">{a.ts}</div>
              <div>
                <div className="font-semibold text-fg-1">{a.actor.name}</div>
                <div className="text-xxs text-fg-3">{a.actor.role}</div>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-fg-1">{a.what}</span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider",
                      a.severity === "info" && "bg-[#E0EFF5] text-[#177FA9]",
                      a.severity === "warn" && "bg-[#FBEFD8] text-[#6B4200]",
                      a.severity === "crit" && "bg-[#FAE3E1] text-[color:var(--danger)]",
                    )}
                  >
                    {a.severity}
                  </span>
                </div>
                <div className="mt-1 text-fg-2">
                  <code className="rounded bg-bg-2 px-1.5 py-0.5 font-mono text-xs">{a.target}</code>
                  <span className="ml-2 text-fg-3">{a.detail}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <p className="mt-4 text-center text-xxs text-fg-3">
        Audit retention: 7 years · entries are append-only and tamper-evident · hashes chained per UTC day.
      </p>
    </div>
  );
}
